import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import PatientForm from "./PatientForm";
import PatientList from "./PatientList";
import AgendaSidebar from "./AgendaSidebar";
import PasswordProtection from "./PasswordProtection";
import "./App.css";
import "./Transitions.css";
import logo from "./joaoribeirologo.png";

const API = "https://backendlab.vercel.app";
const PAGE_SIZE = 50;

const App = () => {
  const [filter, setFilter] = useState("filterAll");
  const [patients, setPatients] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [currentPatient, setCurrentPatient] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [authenticated, setAuthenticated] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState("Todos");
  const [fetchAppointments, setFetchAppointments] = useState(null);

  const [patientCounts, setPatientCounts] = useState({
    Todos: 0,
    "A executar": 0,
    "Precisa Chamada": 0,
    "Foram chamados": 0,
    "Trabalhos Colocados": 0,
  });

  // Pagination state (only used for filterAll, no search, no doctor filter)
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const handleClearSearch = () => setSearchTerm("");

  const isPaginatedMode =
    filter === "filterAll" &&
    selectedDoctor === "Todos" &&
    searchTerm.trim() === "";

  // Fetch counts for the filter badges (lightweight, independent of main list)
  const fetchCounts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/patients/counts`);
      setPatientCounts(response.data);
    } catch (error) {
      console.error("Error fetching counts:", error);
    }
  }, []);

  // Main fetch — handles both paginated (filterAll) and full-fetch modes
  const fetchPatients = useCallback(
    async (pageToLoad = 1, append = false) => {
      try {
        if (isPaginatedMode) {
          const response = await axios.get(`${API}/patients`, {
            params: { page: pageToLoad, limit: PAGE_SIZE },
          });
          const { patients: newPatients, totalPages } = response.data;

          setPatients((prev) =>
            append ? [...prev, ...newPatients] : newPatients
          );
          setPage(pageToLoad);
          setHasMore(pageToLoad < totalPages);
        } else {
          // Full fetch for other filters / search / doctor filter
          const response = await axios.get(`${API}/patients`);
          let filteredData = response.data;

          if (filter !== "filterAll") {
            filteredData = filteredData.filter((p) => p.status === filter);
          }
          if (selectedDoctor !== "Todos") {
            filteredData = filteredData.filter((p) => p.doctorName === selectedDoctor);
          }
          if (searchTerm.trim() !== "") {
            filteredData = filteredData.filter((p) =>
              p.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
          }

          setPatients(filteredData);
          setHasMore(false);
        }
      } catch (error) {
        console.error("Error fetching patients:", error);
      }
    },
    [filter, selectedDoctor, searchTerm, isPaginatedMode]
  );

  // Load next page when scrolling (only relevant in paginated mode)
  const loadMore = useCallback(async () => {
    if (!isPaginatedMode || !hasMore || isLoadingMore) return;
    setIsLoadingMore(true);
    await fetchPatients(page + 1, true);
    setIsLoadingMore(false);
  }, [isPaginatedMode, hasMore, isLoadingMore, page, fetchPatients]);

  const openModal = (patient = null) => {
    setCurrentPatient(patient);
    setModalIsOpen(true);
  };

  // Reset and fetch when filter/doctor/search changes
  useEffect(() => {
    setPage(1);
    setHasMore(true);
    fetchPatients(1, false);
    fetchCounts();
  }, [filter, selectedDoctor, searchTerm, fetchPatients, fetchCounts]);

  // Periodic refresh every 2 minutes — re-fetch what's currently loaded
  useEffect(() => {
    const interval = setInterval(() => {
      if (isPaginatedMode) {
        // Re-fetch all currently loaded pages as a fresh snapshot
        const loadedCount = Math.max(patients.length, PAGE_SIZE);
        axios
          .get(`${API}/patients`, { params: { page: 1, limit: loadedCount } })
          .then((response) => {
            setPatients(response.data.patients);
            setHasMore(1 * loadedCount < response.data.total ? true : false);
          })
          .catch((error) => console.error("Error refreshing patients:", error));
      } else {
        fetchPatients(1, false);
      }
      fetchCounts();
    }, 2 * 60 * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaginatedMode, patients.length, fetchPatients, fetchCounts]);

  const closeModal = () => {
    setModalIsOpen(false);
    setCurrentPatient(null);
  };

  const handleAuthentication = () => setAuthenticated(true);
  const handleFilterChange = (newFilter) => setFilter(newFilter);

  if (!authenticated) {
    return <PasswordProtection onAuthenticated={handleAuthentication} />;
  }

  const showAgenda = filter === "needReady";

  return (
    <div className="app">
      <div className="top-container">
        <img src={logo} alt="Logo" className="logo" onClick={() => handleFilterChange("filterAll")} />
        <div className="filters">
          <button onClick={() => handleFilterChange("filterAll")} className={filter === "filterAll" ? "active-filter" : ""}>Todos</button>
          <button onClick={() => handleFilterChange("needReady")} className={filter === "needReady" ? "active-filter" : ""}>A executar</button>
          <button onClick={() => handleFilterChange("readyToCall")} className={filter === "readyToCall" ? "active-filter" : ""}>Precisa Chamada</button>
          <button onClick={() => handleFilterChange("called")} className={filter === "called" ? "active-filter" : ""}>Foram chamados</button>
          <button onClick={() => handleFilterChange("completed")} className={filter === "completed" ? "active-filter" : ""}>Trabalhos Colocados</button>
        </div>
      </div>

      <div className="search-container">
        <input type="text" placeholder="Procurar Paciente" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="search-input" />
        <button onClick={handleClearSearch}>Apagar</button>
        <select value={selectedDoctor} onChange={(e) => setSelectedDoctor(e.target.value)} className="doctor-filter">
          <option value="Todos">--</option>
          <option value="JP">JP</option>
          <option value="Ricardo">Ricardo</option>
          <option value="João Rua">João Rua</option>
          <option value="Bernardo">Bernardo</option>
          <option value="Sónia">Sónia</option>
          <option value="Maria do Carmo">Maria do Carmo</option>
          <option value="João Pinto">João Pinto</option>
          <option value="Marta Vale">Marta Vale</option>
          <option value="Ana Marques">Ana Marques</option>
          <option value="Miguel Pessanha">Miguel Pessanha</option>
        </select>
      </div>

      <div className="patient-counts-container">
        <span><strong>Todos:</strong> {patientCounts["Todos"]}</span> |
        <span><strong>A executar:</strong> {patientCounts["A executar"]}</span> |
        <span><strong>Precisa Chamada:</strong> {patientCounts["Precisa Chamada"]}</span> |
        <span><strong>Foram chamados:</strong> {patientCounts["Foram chamados"]}</span> |
        <span><strong>Trabalhos Colocados:</strong> {patientCounts["Trabalhos Colocados"]}</span>
      </div>

      <div className={`main-content-area ${showAgenda ? "with-agenda" : ""}`}>
        {showAgenda && (
          <AgendaSidebar onRegisterFetch={(fn) => setFetchAppointments(() => fn)} />
        )}
        <div className="scrollable-container">
          <PatientList
            filter={filter}
            fetchPatients={() => fetchPatients(1, false)}
            openModal={openModal}
            patients={patients}
            selectedDoctor={selectedDoctor}
            onLoadMore={loadMore}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
          />
        </div>
      </div>

      <div className="add-patient-container">
        <button className="add-patient-button" onClick={() => openModal()}>+</button>
      </div>

      <PatientForm
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        fetchPatients={() => fetchPatients(1, false)}
        fetchAppointments={fetchAppointments}
        initialData={currentPatient}
      />
    </div>
  );
};

export default App;